import AddToDashboardButton from './AddToDashboardButton'
import AnalysisCardTitle from './AnalysisCardTitle'
import ActivityVisualization from '../dataVisualizations/ActivityVisualization'
import ParentCompareView from '../dataVisualizations/ParentCompareView'
import periodRangeToDates from '../../helpers/liveReportHelpers/periodRangeToDates'
import { EmptyStateProvider } from '../../StateManagment/EmptyStateContext'

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// Thin wrapper around `ActivityVisualization` — adds the dashboard-specific
// header (title + description + AddToDashboardButton) and the per-item pin
// button. All the visualisation logic (navigator, filter bar, charts) lives
// in the shared component so the live-report ActivityWidget can render the
// same thing without inheriting dashboard chrome.

const computeItemPin = (currentItem, activity) => {
    if (!currentItem) return null
    if (currentItem.kind === 'output') {
        return {
            type: 'outputIndicator',
            activityId: activity.ID,
            outputId: currentItem.data.ID,
            label: `${activity.Activity} — ${currentItem.data.Output || 'Output'}`,
        }
    }
    return {
        type: 'output',
        activityId: activity.ID,
        indicatorId: currentItem.data.ID,
        label: `${activity.Activity} — ${currentItem.data.Title || currentItem.data.Type}`,
    }
}

/**
 * @param {object} props
 * @param {object} props.activity - The Activity doc to render.
 * @param {React.ReactNode} [props.headerAction] - Right-hand header slot (defaults to a card-level pin button).
 * @param {string} [props.titleLink] - Path to the deep analysis; makes the title a link (Analyse page only)
 * @param {string} [props.graphTypeOverride] - Card-level graph-type override.
 * @param {string[]} [props.colorPalette] - Available palette colours.
 * @param {Object<string, string>} [props.colorOverrides] - Per-sub-item colour overrides.
 * @param {Array<object>} [props.personas] - Tenant personas for the filter chip.
 * @param {boolean} [props.personasEnabled] - Whether the persona chip renders.
 * @param {string} [props.selectedPersonaId] - Currently selected persona.
 * @param {(personaId: string) => void} [props.onPersonaChange] - Persona selection handler.
 * @param {object|null} [props.dashboardItem] - DashboardItems doc (dashboard only); enables the per-output / per-event-field options footer.
 */
// Analyse/dashboard cards show the empty-state CTA in their charts.
const EMPTY_STATE_CTX = { type: 'activity', showCta: true }

const AnalysisActivityCardBody = ({
    activity,
    headerAction,
    titleLink,
    graphTypeOverride,
    colorPalette,
    colorOverrides,
    personas,
    personasEnabled,
    selectedPersonaId,
    onPersonaChange,
    dashboardItem = null,
    showOptions = false,
    graphTypeByOutput,
    colorOverridesByOutput,
    colorOverridesByEventField,
    onSaveOutputOptions,
    onSaveEventFieldOptions,
    hidePins = false,
    compareActive = false,
    rangeA = null,
    rangeB = null,
}) => {
    // Analyse page: synthesise a DashboardItems-shaped object from the card's
    // transient option state so ActivityVisualization's existing per-output /
    // per-event-field readers work unchanged; edits route via onSaveOptions.
    const optionsItem = showOptions
        ? {
            GraphTypeByOutput: graphTypeByOutput || {},
            ColorOverridesByOutput: colorOverridesByOutput || {},
            ColorOverridesByEventField: colorOverridesByEventField || {},
        }
        : null
    const handleSaveOptions = (patch, key, kind) => {
        if (kind === 'eventField') onSaveEventFieldOptions?.(patch, key)
        else onSaveOutputOptions?.(patch, key)
    }

    // Deep-analysis page (hidePins): the page's anchor header already shows the
    // title, so the card renders no header of its own. The activity description
    // is intentionally not shown on these cards.
    const headerEl = !hidePins ? (
        <div className="new-dashboard-card-label analysis-card-header">
            <div className="analysis-card-header-text">
                <AnalysisCardTitle title={activity.Activity} titleLink={titleLink} />
            </div>
            {headerAction || (
                <AddToDashboardButton
                    type='activity'
                    activityId={activity.ID}
                    label={activity.Activity}
                    showLabel
                />
            )}
        </div>
    ) : null

    const renderViz = (range, nav) => {
        const { startDate, endDate } = periodRangeToDates(range)
        const compare = !!nav
        return (
            <ActivityVisualization
                activity={activity}
                graphTypeOverride={graphTypeOverride}
                colorPalette={colorPalette}
                colorOverrides={colorOverrides}
                personas={personas}
                personasEnabled={personasEnabled}
                selectedPersonaId={selectedPersonaId}
                onPersonaChange={onPersonaChange}
                dashboardItem={dashboardItem || optionsItem}
                onSaveOptions={showOptions ? handleSaveOptions : undefined}
                startDate={startDate}
                endDate={endDate}
                periodRange={range}
                hideControls={compare}
                visible={compare ? nav.visible : undefined}
                onVisibleChange={compare ? nav.setVisible : undefined}
                onItemsChange={compare ? nav.setItems : undefined}
                renderItemAction={(hidePins || compare) ? undefined : (currentItem, currentActivity) => {
                    const pin = computeItemPin(currentItem, currentActivity)
                    return pin ? <AddToDashboardButton {...pin} /> : null
                }}
            />
        )
    }

    return (
        <EmptyStateProvider value={EMPTY_STATE_CTX}>
            {compareActive ? (
                <ParentCompareView
                    header={headerEl}
                    rangeA={rangeA}
                    rangeB={rangeB}
                    renderViz={(range, side, nav) => renderViz(range, nav)}
                />
            ) : (
                <>
                    {headerEl}
                    {renderViz(rangeA, null)}
                </>
            )}
        </EmptyStateProvider>
    )
}

export default AnalysisActivityCardBody
